import { useContext, useEffect, useState } from 'react'
import { parseJSON } from '@fs/zion-locale/date-fns'
import { getSessionId } from '@fs/zion-session'
import { useZionAxios } from '@fs/zion-axios'
import { AccountLockedUpdateContext } from '../context/AccountLockedProvider'

export default function useMaxSessionAge(
  // maxAgeMinutes should be a little less than the time enforced by backend so that the user has time to make changes
  maxAgeMinutes = 8
) {
  const setAccountLocked = useContext(AccountLockedUpdateContext)
  const [isExpired, setExpired] = useState(null)
  const sessionId = getSessionId()
  const [{ data, response, error }] = useZionAxios(
    {
      // encode just in case session ever uses characters that need encoding
      url: `/service/ident/session/sessions/${encodeURIComponent(sessionId)}`,
      method: 'get',
      headers: {
        accept: 'application/session-v1+json',
      },
    },
    { useCache: false }
  )
  // e.g. "Date: Fri, 17 Sep 2021 20:00:00 GMT"
  const nowHttpDate = response?.headers?.date
  useEffect(() => {
    if (!data) {
      return null
    }
    if (!data.userId || !data.createdTime) {
      setExpired(true)
      return null
    }
    const browserNow = new Date()
    const systemOffset = browserNow - (nowHttpDate ? new Date(nowHttpDate) : browserNow)
    const createdDate = parseJSON(data.createdTime)
    if (getIsExpired({ createdDate, maxAgeMinutes, systemOffset, browserNow })) {
      setExpired(true)
      return null
    }
    setExpired(false)

    const interval = setInterval(() => {
      if (getIsExpired({ createdDate, maxAgeMinutes, systemOffset })) {
        setExpired(true)
      }
      // recalculate every minute (this should be good enough given there is some buffer between the backend timeout)
    }, 60000)
    return () => {
      clearInterval(interval)
    }
  }, [data, maxAgeMinutes, nowHttpDate])

  useEffect(() => {
    if (isExpired !== null) {
      setAccountLocked(isExpired)
    }
  }, [isExpired, setAccountLocked])

  return { isExpired, error, isLoading: !error && isExpired === null }
}

function getIsExpired({ createdDate, maxAgeMinutes, systemOffset, browserNow = new Date() }) {
  const duration = browserNow - createdDate - systemOffset
  return Number.isNaN(duration) || maxAgeMinutes * 60 * 1000 < duration
}
